import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, HeartHandshake, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useSiteContent } from "../hooks/useSiteContent";
import { Reveal, Eyebrow } from "../components/Reveal";

export default function DogsForSale() {
  const content = useSiteContent();
  const pg = content?.pages?.sale || {};
  const [dogs, setDogs] = useState(null);

  useEffect(() => {
    api
      .get("/dogs")
      .then((r) => setDogs(r.data.filter((d) => d.for_sale)))
      .catch(() => setDogs([]));
  }, []);

  const enquiryLink = (dog) =>
    `/contact-us?service=${encodeURIComponent("Dogs For Sale Enquiry")}&dog=${encodeURIComponent(dog.name)}`;

  return (
    <main className="bg-brand-obsidian pt-28 sm:pt-32" data-testid="dogs-for-sale-page">
      <section className="px-4 sm:px-6 pb-16">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> {pg.eyebrow || "Dogs For Sale"}</Eyebrow>
            <h1 className="mt-4 font-display font-extrabold uppercase text-3xl sm:text-5xl text-white tracking-tight">
              {pg.title || "Working dogs,"} <span className="text-brand-crimson">{pg.title_accent || "ready for new homes"}</span>
            </h1>
            <p className="mt-4 max-w-2xl text-slate-400 leading-relaxed">
              {pg.intro || "Occasionally we have fully trained dogs and puppies from our own breeding lines available to approved, experienced homes. Every dog is health-checked, vaccinated and microchipped. Message us for full details and pricing."}
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {dogs === null && (
              <div className="col-span-full flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="sale-dogs-loading-spinner" />
              </div>
            )}
            {dogs !== null && dogs.length === 0 && (
              <div className="col-span-full rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="sale-dogs-empty">
                {pg.empty || "No dogs for sale right now - check back soon, or message us about upcoming litters."}
              </div>
            )}
            {dogs?.map((dog, i) => (
              <Reveal key={dog.id || dog.name} delay={i * 0.07}>
                <div
                  className="group h-full overflow-hidden rounded-lg border border-brand-line bg-brand-surface transition-all duration-300 hover:border-brand-crimson/70 hover:shadow-[0_0_40px_rgba(200,16,46,0.15)]"
                  data-testid={`sale-dog-card-${(dog.name || "dog").toLowerCase()}`}
                >
                  <div className="relative h-60 overflow-hidden">
                    <img src={dog.photo} alt={`${dog.name} - ${dog.breed}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-obsidian via-transparent to-transparent" />
                    {dog.price && (
                      <span className="absolute top-3 right-3 bg-brand-crimson text-white font-eyebrow text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 rounded-md" data-testid={`sale-dog-price-${(dog.name || "dog").toLowerCase()}`}>
                        {dog.price}
                      </span>
                    )}
                  </div>
                  <div className="p-5 flex flex-col">
                    <h3 className="font-display text-xl font-bold text-white">{dog.name}</h3>
                    <p className="mt-1 text-sm text-slate-400">{dog.breed}</p>
                    {dog.description && <p className="mt-3 text-sm text-slate-300 leading-relaxed">{dog.description}</p>}
                    <Link
                      to={enquiryLink(dog)}
                      data-testid={`sale-dog-enquire-${(dog.name || "dog").toLowerCase()}`}
                      className="mt-5 inline-flex items-center justify-center gap-2 bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold px-5 py-3 rounded-md transition-all duration-300 hover:shadow-[0_0_24px_rgba(200,16,46,0.5)]"
                    >
                      {pg.card_button || "Message for Details"} <MessageSquare className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-lg border border-brand-crimson/40 bg-brand-surface p-8 sm:p-12 grain" data-testid="rehoming-section">
              <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-brand-crimson/15 blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-brand-crimson/15 border border-brand-crimson/30 text-brand-crimson">
                  <HeartHandshake className="h-6 w-6" />
                </div>
                <h2 className="mt-6 font-display font-bold text-2xl sm:text-4xl text-white tracking-tight">
                  {pg.rehome_title || "Can't house your dog anymore?"} <span className="text-brand-crimson">{pg.rehome_accent || "We'll take it."}</span>
                </h2>
                <p className="mt-4 max-w-2xl text-slate-300 leading-relaxed">
                  {pg.rehome_text || "Life changes, and sometimes a much-loved dog can no longer stay with you. If you're struggling to house your dog, message us - we'll assess them, bring on any training they need, and give them a working home with people who understand the breed. No judgement, just a safe landing."}
                </p>
                <Link
                  to={`/contact-us?service=${encodeURIComponent("Rehoming - I can't house my dog anymore")}`}
                  data-testid="rehoming-cta"
                  className="mt-7 inline-flex items-center gap-2 bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-xs uppercase tracking-[0.2em] font-bold px-7 py-4 rounded-md transition-all duration-300 hover:shadow-[0_0_32px_rgba(200,16,46,0.55)]"
                >
                  {pg.rehome_button || "Ask Us to Take Your Dog"} <MessageSquare className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
